import type { ComponentProps, HTMLAttributes, PropsWithChildren, ReactNode } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, type To } from 'react-router'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Skeleton } from '@/components/ui'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { AtendimentoTrigger } from '@/features/atendimento/shared/components/atendimento-trigger'
import { cn } from '@/infra'

import { getCurrentLogoPath } from '../../environment.config'

export interface IBreadcrumbItem extends Omit<ComponentProps<typeof Link>, 'to'> {
  label: string
  to?: To
  dropdownItems?: Array<{ label: string }>
}

interface IPageContentLayoutProps extends HTMLAttributes<HTMLDivElement> {
  breadcrumb?: IBreadcrumbItem[]
  disableBreadcrumb?: boolean
  atendimentoTrigger?: ReactNode
}

interface IBreadcrumbCompProps extends ComponentProps<typeof BreadcrumbItem> {
  item: IBreadcrumbItem
  disableBreadcrumb?: boolean
  index?: number
  breadcrumbs?: IBreadcrumbItem[]
}

const BreadcrumbCompMobile = ({
  item,
  index,
  breadcrumbs,
  children,
  ...props
}: PropsWithChildren<IBreadcrumbCompProps>) => {
  if (!breadcrumbs) return null
  const isUltimoItemBreadcrumb = index === breadcrumbs.length - 1
  if (!isUltimoItemBreadcrumb) return null
  const PENULTIMO_ITEM_BREADCRUMB = breadcrumbs.at(-2)
  if (!PENULTIMO_ITEM_BREADCRUMB?.to) return null

  return (
    <div className="bg-foreground mb-6 w-full">
      <BreadcrumbItem key={item.label} {...props} className="w-full">
        <Render.If condition={PENULTIMO_ITEM_BREADCRUMB.to}>
          {(to) => (
            <BreadcrumbLink
              {...item}
              to={to}
              state={PENULTIMO_ITEM_BREADCRUMB.state}
              className="flex w-full items-center justify-center gap-4 px-4 py-3"
            >
              <Icon name="arrow-left" className="text-primary" size={26} />
              <span className="-ml-10 flex-1 text-center text-base uppercase">{item.label}</span>
            </BreadcrumbLink>
          )}
        </Render.If>
      </BreadcrumbItem>
      {children}
    </div>
  )
}

const BreadcrumbCompWeb = ({
  item,
  children,
  disableBreadcrumb,
  ...props
}: PropsWithChildren<IBreadcrumbCompProps>) => {
  if (!item?.dropdownItems?.length) {
    return (
      <>
        <BreadcrumbItem key={item.label} {...props}>
          <Render.If
            condition={item.to}
            fallback={
              <BreadcrumbPage data-disabled={disableBreadcrumb} className="data-[disabled=true]:text-muted-foreground">
                {item.label}
              </BreadcrumbPage>
            }
          >
            {(to) => (
              <BreadcrumbLink {...item} to={to}>
                {item.label}
              </BreadcrumbLink>
            )}
          </Render.If>
        </BreadcrumbItem>
        {children}
      </>
    )
  }

  return (
    <>
      <BreadcrumbItem key={item.label} {...props}>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1">
            <BreadcrumbEllipsis className="h-4 w-4" />
            <span className="sr-only">Toggle menu</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {item.dropdownItems?.map((dropdownItem) => (
              <DropdownMenuItem key={dropdownItem.label}>{dropdownItem.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </BreadcrumbItem>
      {children}
    </>
  )
}

const MODE = import.meta.env.MODE

const PageLayout = ({
  breadcrumb,
  children,
  className,
  disableBreadcrumb,
  atendimentoTrigger = <AtendimentoTrigger />,
  ...props
}: PropsWithChildren<IPageContentLayoutProps>) => {
  const { t } = useTranslation('common')
  const _breadcrumb = breadcrumb ? [{ label: t('inicio'), to: '/' }, ...breadcrumb] : null
  return (
    <div className="px-0 md:px-4">
      <header className="flex h-16 shrink-0 items-center justify-between gap-2 px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-0">
        <div className="flex h-5 items-center gap-4 text-sm">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" />
          <Link to="/" className="transition-transform hover:scale-105">
            <img src={getCurrentLogoPath(MODE)} alt="Logo Ventas - Grupo Plaenge" className="max-h-4" />
          </Link>
        </div>
        {atendimentoTrigger}
      </header>

      <Render.If condition={_breadcrumb}>
        {(bc) => (
          <>
            <div className="md:hidden">
              <BreadcrumbList>
                {bc.map((item, index, lista) => (
                  <BreadcrumbCompMobile key={item.label} breadcrumbs={lista} index={index} item={item} />
                ))}
              </BreadcrumbList>
            </div>
            <Breadcrumb className="mb-4 hidden px-4 uppercase md:block" data-testid="breadcrumb">
              <BreadcrumbList>
                {bc.map((item, index) => (
                  <BreadcrumbCompWeb
                    key={item.label}
                    item={item}
                    disableBreadcrumb={index < bc.length - 1 ? disableBreadcrumb : false}
                  >
                    {index < bc.length - 1 && <BreadcrumbSeparator />}
                  </BreadcrumbCompWeb>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </>
        )}
      </Render.If>

      <div className={cn('sm:p-4 md:py-4', className)} {...props}>
        {children}
      </div>
    </div>
  )
}

const PageLayoutSkeleton = ({ showBreadcrumb = true, children }: PropsWithChildren<{ showBreadcrumb?: boolean }>) => {
  return (
    <div className="px-4">
      <header className="flex h-14 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex h-4 items-center gap-4 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="bg-muted h-5 w-px" />
          <Link to="/" className="transition-transform hover:scale-105">
            <img src={getCurrentLogoPath(MODE)} alt="Logo Ventas - Grupo Plaenge" className="w-20.5" />
          </Link>
        </div>
        <Skeleton className="h-10 w-47 rounded-md" />
      </header>
      <Render.If condition={showBreadcrumb}>
        {() => (
          <div className="mt-2 mb-4 flex items-center gap-2 text-xs uppercase">
            <Skeleton className="h-5 w-16" />
            <Icon name="chevron-right" className="text-muted-foreground" size={16} />
            <Skeleton className="h-5 w-32" />
            <Icon name="chevron-right" className="text-muted-foreground" size={16} />
            <Skeleton className="h-5 w-24" />
          </div>
        )}
      </Render.If>
      {children}
    </div>
  )
}

export const PageContainer = ({ children }: ComponentProps<'div'>) => {
  return <div className="mx-auto min-h-screen w-full max-w-4xl">{children}</div>
}

export const PageHeader = ({ children }: ComponentProps<'div'>) => {
  return <div className="px-6 pt-0 pb-6 md:mb-8 md:px-0 md:pt-0 md:pb-0">{children}</div>
}

export const PageTitle = ({ children, className }: ComponentProps<'h1'>) => {
  return (
    <h1 className={cn('mb-2 text-xl font-normal tracking-wide text-neutral-700 uppercase', className)}>{children}</h1>
  )
}

export const PageSubtitle = ({ children }: ComponentProps<'p'>) => {
  return <p className="text-sm tracking-wide text-neutral-500">{children}</p>
}

export const PageContent = ({ children, className }: ComponentProps<'section'>) => {
  return <section className={cn('border-muted border bg-white px-4 py-6', className)}>{children}</section>
}

export const Page = Object.assign(PageLayout, {
  Main: PageContainer,
  Header: PageHeader,
  Title: PageTitle,
  Description: PageSubtitle,
  Section: PageContent,
})

export { PageLayout, PageLayoutSkeleton }
